import React, { FC, useEffect, useRef } from 'react';
import moment from 'moment-timezone';

import { Field } from 'redux-form';
import { TranslationKey, useTranslations } from '@lang';
import { isTimestampsFromSameDay, parseDateToString } from '@utils/helpers';
import { gridTheme } from '@utils/grid';
import { UnavailableBox } from '@common/components';
import { usePageContext } from '@common/components/PageContext';
import { DatesSlider } from '@common/components/DatesSlider';
import { StartDatePicker } from '@common/components/StartDatePicker';
import { Heading } from '@common/components/BookingForms';
import { FormBox, FormWrapper } from './Availability.styles';
import { AvailabilityProps } from './Availability.types';
import { TimeSlotSelector } from './TimeSlotSelector';

import {
  getAvailability,
  getSliderDates,
  startDateAfterChange,
  visitDayAfterChange,
} from './helpers';
import { useIsWindowWiderThan } from '@common/hooks';
import { EntityTypes } from '@common-types/Entities';
import { analytics } from '../analytics';
import { useGetSpaSettings } from '../spa/hooks';

export const Availability: FC<AvailabilityProps> = ({
  change,
  fetchAvailability,
  changeJourneyParam,
  allTimeslots,
  isModalOpen,
  handleSubmit,
  spaEntity,
  visitDate,
  maxDaysAdvance = null,
  reservedSpaTimeSlots,
  initialValues,
  startDate,
  entityType,
  fetchingDates,
  property,
  setStartDate,
}) => {
  useEffect(() => {
    fetchAvailability &&
      getAvailability(initialValues.startDate, fetchAvailability, change, entityType);
  }, []);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { phoneNumber } = spaSettings || {};

  const { t } = useTranslations();
  const { parentPageName } = usePageContext();
  const { telephoneOnly } = spaEntity;

  const isDesktop = useIsWindowWiderThan(gridTheme.breakpoints.md);
  const availableTimeOptions =
    (allTimeslots?.length > 0 &&
      allTimeslots.filter(
        (time) => !reservedSpaTimeSlots.includes(time.startDateTime as string)
      )) ||
    [];

  const arrivalTimesOptions =
    availableTimeOptions
      .filter((option) => isTimestampsFromSameDay(option.startDateTime, visitDate))
      .map((option) => ({
        ...option,
        startDateTime: option.startDateTime,
        value: option.startDateTime,
      })) || [];

  const hasFired = useRef(false);

  if (
    allTimeslots.length &&
    !fetchingDates?.length &&
    !availableTimeOptions.filter((option) =>
      isTimestampsFromSameDay(option.startDateTime, startDate)
    ).length
  ) {
    analytics.track({
      event: 'denial',
      params: {
        start_date: moment(startDate).format('YYYY-MM-DD').toString(),
        end_date: '',
        items: [
          {
            item_category:
              entityType === EntityTypes.TREATMENT ? 'spa-treatments' : 'spa-facilities',
            item_id: `${spaEntity.id}`,
            item_name: spaEntity.name,
          },
        ],
      },
    });
    hasFired.current = true;
  }

  useEffect(() => {
    if (fetchingDates?.length) {
      hasFired.current = false;
    }
  }, [fetchingDates]);
  const timeslotLabel: TranslationKey =
    entityType === EntityTypes.TREATMENT ? 'treatment.selectArrival' : 'spa.selectArrival';
  return (
    <form onSubmit={handleSubmit} noValidate>
      <FormWrapper>
        <Heading>{t('treatment.availabilityStepHeader')}</Heading>
        <FormBox>
          <Field
            component={StartDatePicker}
            name="startDate"
            handleAfterChange={(sDate) => {
              startDateAfterChange(
                sDate,
                change,
                changeJourneyParam,
                fetchAvailability,
                entityType
              );
              setStartDate && setStartDate(parseDateToString(sDate));
            }}
            popperModifiers={{
              preventOverflow: {
                enabled: true,
                escapeWithReference: false,
                boundariesElement: 'viewport',
              },
            }}
            maxDate={maxDaysAdvance ? moment().add(maxDaysAdvance, 'days') : null}
          />
          {isModalOpen && (
            <Field
              key={startDate && startDate.format('YYYY-MM-DD')}
              component={DatesSlider}
              name="visitDate"
              handleAfterChange={(date) => visitDayAfterChange(date, change, changeJourneyParam)}
              dates={getSliderDates(
                allTimeslots,
                startDate ? startDate.format() : moment().format()
              )}
              hasPrices
              visitDate={visitDate}
              loadingDates={fetchingDates}
              telephoneOnly={spaEntity.telephoneOnly}
            />
          )}
          <TimeSlotSelector
            arrivalTimesOptions={arrivalTimesOptions}
            change={change}
            disabled={!!fetchingDates && fetchingDates.length > 0}
            labelTranslation={timeslotLabel}
            showPerPerson={entityType === EntityTypes.FACILITY}
          />
        </FormBox>
        {telephoneOnly && (
          <UnavailableBox
            title={t('spa.telephoneOnly', { spaEntity: parentPageName })}
            body={t('spa.callUsOn', {
              phoneNumber: isDesktop ? (
                phoneNumber
              ) : (
                <a href={`tel: ${phoneNumber}`}>{phoneNumber}</a>
              ),
            })}
          />
        )}
      </FormWrapper>
    </form>
  );
};
